import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Music, SkipBack, SkipForward, Play, Pause, Volume2, Globe, MapPin } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Dock } from '@/components/ui/Dock';

const PLAYLIST = [
  'AWb0Z5yA1XA',
  'aCynyt9z8CQ',
  'mQQKZ5cgybU',
  'RLXYI1J5Dfw',
  'tzBEpdfCRqw',
];

const TRACK_NAMES = [
  'Hymn of the Knights',
  'Templar March',
  'Salve Regina',
  'Da Pacem Domine',
  'Veni Creator Spiritus',
];

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: (() => void) | undefined;
  }
}

let apiLoaded = false;
let apiReady = false;
const readyCallbacks: (() => void)[] = [];

function loadYTApi(cb: () => void) {
  if (apiReady) { cb(); return; }
  readyCallbacks.push(cb);
  if (apiLoaded) return;
  apiLoaded = true;
  const prev = window.onYouTubeIframeAPIReady;
  window.onYouTubeIframeAPIReady = () => {
    prev?.();
    apiReady = true;
    readyCallbacks.forEach(fn => fn());
    readyCallbacks.length = 0;
  };
  const s = document.createElement('script');
  s.src = 'https://www.youtube.com/iframe_api';
  document.head.appendChild(s);
}

const WA_NUMBER = '5521999999999'; // ajuste o número real
const WA_MSG = encodeURIComponent('Olá! Gostaria de mais informações sobre o Encontro Nacional Templário 2026.');

export const BackgroundMusic: React.FC = () => {
  const { t, i18n } = useTranslation();
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(false);
  const [started, setStarted] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [isLangExpanded, setIsLangExpanded] = useState(false);
  const [volume, setVolume] = useState(40);
  const trackIdxRef = useRef(0);

  const loadTrack = useCallback((idx: number) => {
    trackIdxRef.current = idx;
    setTrackIdx(idx);
    playerRef.current?.loadVideoById(PLAYLIST[idx]);
  }, []);

  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    loadYTApi(() => {
      if (!containerRef.current || playerRef.current) return;
      const div = document.createElement('div');
      div.id = 'yt-bg-music';
      containerRef.current.appendChild(div);

      playerRef.current = new window.YT.Player('yt-bg-music', {
        height: '1',
        width: '1',
        videoId: PLAYLIST[0],
        playerVars: {
          autoplay: 0,
          controls: 0,
          disablekb: 1,
          fs: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
        },
        events: {
          onReady: () => {
            playerRef.current.setVolume(40);
            setIsReady(true);
          },
          onStateChange: (e: any) => {
            if (e.data === window.YT.PlayerState.PLAYING) {
              setPlaying(true);
            } else if (e.data === window.YT.PlayerState.PAUSED) {
              setPlaying(false);
            } else if (e.data === window.YT.PlayerState.ENDED) {
              const next = (trackIdxRef.current + 1) % PLAYLIST.length;
              loadTrack(next);
            }
          },
        },
      });
    });
  }, [loadTrack]);

  const handleStart = useCallback(() => {
    if (started || !isReady) return;
    setStarted(true);
    setExpanded(true);
    setPlaying(true);
    if (playerRef.current && typeof playerRef.current.playVideo === 'function') {
      playerRef.current.playVideo();
    }
  }, [started, isReady]);

  useEffect(() => {
    trackIdxRef.current = trackIdx;
  }, [trackIdx]);

  const toggle = useCallback(() => {
    if (!playerRef.current) return;
    const state = playerRef.current.getPlayerState();
    if (state === window.YT.PlayerState.PLAYING) {
      playerRef.current.pauseVideo();
    } else {
      playerRef.current.playVideo();
    }
  }, []);

  const nextTrack = useCallback(() => {
    const next = (trackIdxRef.current + 1) % PLAYLIST.length;
    loadTrack(next);
  }, [loadTrack]);

  const prevTrack = useCallback(() => {
    const prev = (trackIdxRef.current - 1 + PLAYLIST.length) % PLAYLIST.length;
    loadTrack(prev);
  }, [loadTrack]);

  const handleVolume = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = Number(e.target.value);
    setVolume(v);
    playerRef.current?.setVolume(v);
  }, []);

  const scrollTo = (id: string) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const currentTrackName = TRACK_NAMES[trackIdx] || `Faixa ${trackIdx + 1}`;

  const toggleLang = () => {
    setIsLangExpanded(!isLangExpanded);
    if (!isLangExpanded) setExpanded(false);
  };

  const toggleMusic = () => {
    if (!started) {
      handleStart();
    } else {
      setExpanded(!expanded);
      if (!expanded) setIsLangExpanded(false);
    }
  };

  const items = [
    { icon: <MapPin size={20} />, label: t('nav.local', 'Local'), onClick: () => window.open('https://maps.google.com/?q=R.%20Riachuelo%2C%20124%20-%20Centro%2C%20Rio%20de%20Janeiro%20-%20RJ%2C%2020230-014', '_blank') },
    { type: 'separator' },
    { icon: <Globe size={20} />, label: t('language', 'Idioma'), isActive: isLangExpanded, onClick: toggleLang },
    { type: 'separator' },
    {
      icon: (
        <svg viewBox="0 0 24 24" fill="currentColor" className="h-[20px] w-[20px]">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
        </svg>
      ),
      label: 'Suporte',
      onClick: () => window.open(`https://wa.me/${WA_NUMBER}?text=${WA_MSG}`, '_blank')
    },
    { type: 'separator' },
    {
      icon: <Music size={20} />,
      label: started ? (playing ? '♫ Tocando' : 'Música Pausada') : 'Música',
      isActive: started && playing,
      onClick: toggleMusic
    }
  ];

  const langs = [
    { code: 'pt', label: 'Português', flag: 'br.png' },
    { code: 'es', label: 'Castellano', flag: 'es.png' },
    { code: 'en', label: 'English', flag: 'us.png' },
    { code: 'fr', label: 'Français', flag: 'fr.png' },
  ];

  return (
    <>
      <div
        ref={containerRef}
        style={{
          position: 'fixed',
          width: 1,
          height: 1,
          overflow: 'hidden',
          opacity: 0,
          pointerEvents: 'none',
          zIndex: -1,
        }}
      />

      <div className="fixed bottom-[110px] left-1/2 -translate-x-1/2 z-[60] pointer-events-none">
        {/* Language selector panel */}
        <div
          className="absolute bottom-0 left-1/2 transition-all duration-300 ease-in-out pointer-events-auto shadow-xl"
          style={{
            maxHeight: isLangExpanded ? 300 : 0,
            opacity: isLangExpanded ? 1 : 0,
            transform: isLangExpanded ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)',
            overflow: 'hidden',
            borderRadius: '24px',
            background: 'rgba(10, 10, 12, 0.85)',
            backdropFilter: 'blur(32px) saturate(200%)',
            border: '1px solid rgba(201, 168, 76, 0.3)',
            boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.7), inset 0 1px 1px rgba(255, 255, 255, 0.03)'
          }}
        >
          <div className="flex min-w-[200px] flex-col gap-1 p-2">
            {langs.map((l) => (
              <button
                key={l.code}
                onClick={() => {
                  i18n.changeLanguage(l.code);
                  setIsLangExpanded(false);
                }}
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-colors text-sm font-semibold",
                  (i18n.language === l.code || i18n.language?.startsWith(l.code)) 
                    ? "bg-[#c9a84c]/20 text-[#c9a84c]" 
                    : "text-neutral-300 hover:bg-white/5 hover:text-[#c9a84c]"
                )}
              >
                <img src={`https://flagcdn.com/w40/${l.flag}`} alt={l.label} className="w-5 h-4 object-cover rounded-sm" />
                {l.label}
              </button>
            ))}
          </div>
        </div>

        {/* Music selector panel */}
        {started && (
          <div
            className="absolute bottom-0 left-1/2 transition-all duration-300 ease-in-out pointer-events-auto shadow-xl"
            style={{
              maxHeight: expanded ? 200 : 0,
              opacity: expanded ? 1 : 0,
              transform: expanded ? 'translateX(-50%) translateY(0)' : 'translateX(-50%) translateY(10px)',
              overflow: 'hidden',
              borderRadius: '24px',
              background: 'rgba(10, 10, 12, 0.85)',
              backdropFilter: 'blur(32px) saturate(200%)',
              border: '1px solid rgba(201, 168, 76, 0.3)',
              boxShadow: '0 20px 50px -10px rgba(0, 0, 0, 0.7), inset 0 1px 1px rgba(255, 255, 255, 0.03)'
            }}
          >
            <div className="flex min-w-[240px] flex-col gap-2 px-4 py-3 w-full h-full">
              <div className="truncate text-xs font-medium tracking-wide text-[var(--gold)]">
                {currentTrackName}
                <span className="ml-1 text-[10px] opacity-50">
                  {trackIdx + 1}/{PLAYLIST.length}
                </span>
              </div>

              <div className="flex items-center justify-center gap-3">
                <button onClick={prevTrack} className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--gold)]/70 transition-colors hover:text-[var(--gold)]">
                  <SkipBack size={16} fill="currentColor" />
                </button>
                <button onClick={toggle} className="flex h-9 w-9 items-center justify-center rounded-full border border-[var(--gold)] text-[var(--gold)] transition-all hover:bg-[var(--gold)]/10">
                  {playing ? <Pause size={16} fill="currentColor" /> : <Play size={16} fill="currentColor" />}
                </button>
                <button onClick={nextTrack} className="flex h-8 w-8 items-center justify-center rounded-full text-[var(--gold)]/70 transition-colors hover:text-[var(--gold)]">
                  <SkipForward size={16} fill="currentColor" />
                </button>
              </div>

              <div className="flex items-center gap-2">
                <Volume2 size={14} className="shrink-0 text-[var(--gold)]/50" />
                <input
                  type="range"
                  min={0}
                  max={100}
                  value={volume}
                  onChange={handleVolume}
                  className="h-1 w-full cursor-pointer appearance-none rounded-full bg-[var(--gold)]/20 accent-[var(--gold)] [&::-webkit-slider-thumb]:h-3 [&::-webkit-slider-thumb]:w-3 [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--gold)]"
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <Dock items={items} />
    </>
  );
};
